import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldQuestion, HeartPulse, CheckCircle2, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_QUESTIONS = [
  {
    q: "What is your primary investment goal?",
    opts: [
      { l: "Preserve capital — avoid losses", v: 1 },
      { l: "Earn steady income", v: 2 },
      { l: "Moderate growth with some volatility", v: 3 },
      { l: "Maximize long-term growth, accept volatility", v: 4 },
    ],
  },
  {
    q: "What is your investment horizon?",
    opts: [
      { l: "Less than 1 year", v: 1 },
      { l: "1 to 3 years", v: 2 },
      { l: "3 to 7 years", v: 3 },
      { l: "More than 7 years", v: 4 },
    ],
  },
  {
    q: "If your portfolio dropped 20% in 3 months, you would…",
    opts: [
      { l: "Sell everything to avoid further losses", v: 1 },
      { l: "Sell a portion to reduce risk", v: 2 },
      { l: "Hold and wait for recovery", v: 3 },
      { l: "Buy more at lower prices", v: 4 },
    ],
  },
  {
    q: "Which return scenario would you prefer?",
    opts: [
      { l: "+6% best / -1% worst", v: 1 },
      { l: "+12% best / -5% worst", v: 2 },
      { l: "+18% best / -12% worst", v: 3 },
      { l: "+25% best / -20% worst", v: 4 },
    ],
  },
  {
    q: "How much of your savings can you invest?",
    opts: [
      { l: "Less than 10%", v: 1 },
      { l: "10–25%", v: 2 },
      { l: "25–50%", v: 3 },
      { l: "More than 50%", v: 4 },
    ],
  },
  {
    q: "How would you describe your investing experience?",
    opts: [
      { l: "None — beginner", v: 1 },
      { l: "FDs / RDs only", v: 2 },
      { l: "Mutual funds and SIPs", v: 3 },
      { l: "Stocks, MFs, alternatives — active investor", v: 4 },
    ],
  },
  {
    q: "How stable is your income?",
    opts: [
      { l: "Variable / freelance", v: 1 },
      { l: "Salaried but recent", v: 2 },
      { l: "Stable salaried 5+ years", v: 3 },
      { l: "Multiple stable income sources", v: 4 },
    ],
  },
  {
    q: "What is your age?",
    opts: [
      { l: "Above 60", v: 1 },
      { l: "45 to 60", v: 2 },
      { l: "30 to 45", v: 3 },
      { l: "Below 30", v: 4 },
    ],
  },
];

const HEALTH_QUESTIONS = [
  "Do you have a clearly defined financial goal for each investment?",
  "Is your portfolio diversified across equity, debt and gold?",
  "Do you review your portfolio at least once a year?",
  "Do you have 6+ months of emergency fund in liquid instruments?",
  "Do you have adequate term and health insurance separate from investments?",
  "Do you maximize Section 80C through ELSS or equity-linked options?",
  "Is your retirement plan on track for your desired post-retirement lifestyle?",
  "Are you free from over-concentration in any single stock or sector (>30%)?",
];

const HEALTH_OPTS = [
  { l: "Yes", v: 2 },
  { l: "Partially", v: 1 },
  { l: "No / Not sure", v: 0 },
];

export default function Quizzes() {
  return (
    <section id="quizzes" data-testid="quizzes-section" className="section relative bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37]">Self-Assessment</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white mt-4 leading-[1.05]">
            Know your <span className="text-gold-gradient">profile.</span>
          </h2>
          <p className="mt-5 text-gray-400 max-w-2xl mx-auto">
            Two short assessments to identify your risk appetite and the health of your existing portfolio.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <RiskQuiz />
          <HealthQuiz />
        </div>
      </div>
    </section>
  );
}

function RiskQuiz() {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const total = RISK_QUESTIONS.length;
  const done = Object.keys(answers).length;
  const allAnswered = done === total;

  const submit = async () => {
    if (!allAnswered) {
      toast.error(`Please answer all ${total} questions before submitting.`);
      return;
    }
    setSubmitting(true);
    const list = Array.from({ length: total }, (_, i) => answers[i]);
    try {
      const { data } = await axios.post(`${API}/risk-profile`, { answers: list });
      setResult(data);
      // Scroll the result into view
      setTimeout(() => {
        document.getElementById("risk-quiz-result")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } catch (err) {
      toast.error("Could not compute. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setResult(null);
  };

  return (
    <div className="glass rounded-3xl p-6 md:p-8" data-testid="risk-quiz">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#D4AF37] flex items-center justify-center">
          <ShieldQuestion size={20} />
        </div>
        <h3 className="font-display font-semibold text-xl text-white">Risk Profile Questionnaire</h3>
      </div>
      <p className="text-sm text-gray-400 mb-6">Answer all {total} questions to discover your investor profile.</p>

      <div className="flex items-center gap-2 mb-6">
        <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${(done / total) * 100}%` }} />
        </div>
        <span className="text-xs text-gray-400 font-medium">{done}/{total}</span>
      </div>

      {!result ? (
        <div className="space-y-7">
          {RISK_QUESTIONS.map((qa, qi) => (
            <div key={qi} data-testid={`risk-question-${qi}`}>
              <p className="font-display text-base text-white leading-snug mb-3">
                <span className="text-[#D4AF37] mr-2">{qi + 1}.</span>
                {qa.q}
              </p>
              <div className="space-y-2">
                {qa.opts.map((o) => {
                  const selected = answers[qi] === o.v;
                  return (
                    <button
                      key={o.v}
                      onClick={() => setAnswers((a) => ({ ...a, [qi]: o.v }))}
                      data-testid={`risk-q${qi}-opt-${o.v}`}
                      aria-pressed={selected}
                      className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm transition ${
                        selected
                          ? "bg-[#D4AF37]/15 border-[#D4AF37] text-white"
                          : "bg-black/30 border-white/10 text-gray-300 hover:border-[#D4AF37]/40"
                      }`}
                    >
                      {o.l}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={submit}
            disabled={submitting || !allAnswered}
            data-testid="risk-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[#D4AF37] text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#FFD700] transition shadow-[0_15px_45px_-10px_rgba(212,175,55,0.55)]"
          >
            {submitting ? "Calculating..." : allAnswered ? "See My Risk Profile" : `Answer all ${total} questions to submit`}
          </button>
        </div>
      ) : (
        <ResultBlock id="risk-quiz-result" testId="risk-result" result={result} onReset={reset} />
      )}
    </div>
  );
}

function HealthQuiz() {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const total = HEALTH_QUESTIONS.length;
  const done = Object.keys(answers).length;
  const allAnswered = done === total;

  const submit = async () => {
    if (!allAnswered) {
      toast.error(`Please answer all ${total} questions before submitting.`);
      return;
    }
    setSubmitting(true);
    const list = Array.from({ length: total }, (_, i) => answers[i]);
    try {
      const { data } = await axios.post(`${API}/portfolio-health`, { answers: list });
      setResult(data);
      setTimeout(() => {
        document.getElementById("health-quiz-result")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } catch (err) {
      toast.error("Could not compute. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setResult(null);
  };

  return (
    <div className="glass rounded-3xl p-6 md:p-8" data-testid="health-quiz">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#D4AF37] flex items-center justify-center">
          <HeartPulse size={20} />
        </div>
        <h3 className="font-display font-semibold text-xl text-white">Portfolio Health Score</h3>
      </div>
      <p className="text-sm text-gray-400 mb-6">Quick 8-point check on the state of your existing portfolio.</p>

      <div className="flex items-center gap-2 mb-6">
        <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${(done / total) * 100}%` }} />
        </div>
        <span className="text-xs text-gray-400 font-medium">{done}/{total}</span>
      </div>

      {!result ? (
        <div className="space-y-6">
          {HEALTH_QUESTIONS.map((q, qi) => (
            <div key={qi} data-testid={`health-question-${qi}`}>
              <p className="font-display text-base text-white leading-snug mb-3">
                <span className="text-[#D4AF37] mr-2">{qi + 1}.</span>
                {q}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {HEALTH_OPTS.map((o) => {
                  const selected = answers[qi] === o.v;
                  return (
                    <button
                      key={o.v}
                      onClick={() => setAnswers((a) => ({ ...a, [qi]: o.v }))}
                      data-testid={`health-q${qi}-opt-${o.v}`}
                      aria-pressed={selected}
                      className={`px-3 py-2 rounded-xl border text-sm transition ${
                        selected
                          ? "bg-[#D4AF37]/15 border-[#D4AF37] text-white"
                          : "bg-black/30 border-white/10 text-gray-300 hover:border-[#D4AF37]/40"
                      }`}
                    >
                      {o.l}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={submit}
            disabled={submitting || !allAnswered}
            data-testid="health-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[#D4AF37] text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#FFD700] transition shadow-[0_15px_45px_-10px_rgba(212,175,55,0.55)]"
          >
            {submitting ? "Calculating..." : allAnswered ? "See My Portfolio Health" : `Answer all ${total} questions to submit`}
          </button>
        </div>
      ) : (
        <ResultBlock id="health-quiz-result" testId="health-result" result={result} onReset={reset} />
      )}
    </div>
  );
}

function ResultBlock({ id, testId, result, onReset }) {
  return (
    <motion.div
      id={id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={testId}
      className="text-center py-4"
    >
      <CheckCircle2 size={44} className="text-[#D4AF37] mx-auto" />
      <div className="text-xs uppercase tracking-[0.3em] text-gray-500 mt-4">Your result</div>
      <div className="font-display font-bold text-4xl text-gold-gradient mt-2">
        {result.category || result.grade}
      </div>
      <div className="text-2xl font-display font-bold text-white mt-1">
        {result.percent ?? result.score}<span className="text-sm text-gray-500">/100</span>
      </div>
      <p className="text-sm text-gray-400 mt-4 leading-relaxed max-w-md mx-auto">{result.summary}</p>
      <button
        onClick={onReset}
        data-testid={`${testId}-restart`}
        className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/5 border border-white/10 text-gray-200 text-sm hover:border-[#D4AF37]/40 transition"
      >
        <RotateCcw size={14} /> Take again
      </button>
    </motion.div>
  );
}
